import { create } from "zustand";

export type BackgroundTaskState = "queued" | "running" | "succeeded" | "failed";

export interface BackgroundTask {
  id: string;
  kind: string;
  title: string;
  state: BackgroundTaskState;
  subjectType: string | null;
  subjectId: string | null;
  parentTaskId: string | null;
  errorMessage: string | null;
  result: Record<string, unknown> | null;
  startedAt: string | null;
  finishedAt: string | null;
  updatedAt: string;
}

const RECENT_TERMINAL_MS = 5 * 60 * 1000;

interface BackgroundTasksState {
  tasksById: Record<string, BackgroundTask>;
  setInitial: (tasks: BackgroundTask[]) => void;
  upsert: (task: BackgroundTask) => void;
  reconcile: (tasks: BackgroundTask[]) => void;
  prune: () => void;
}

function isVisible(task: BackgroundTask, now: number): boolean {
  if (task.state === "queued" || task.state === "running") return true;
  if (!task.finishedAt) return true;
  return now - new Date(task.finishedAt).getTime() < RECENT_TERMINAL_MS;
}

export const useBackgroundTasksStore = create<BackgroundTasksState>((set, get) => ({
  tasksById: {},
  setInitial: (tasks) => {
    set((state) => {
      const next = { ...state.tasksById };
      for (const t of tasks) {
        const existing = next[t.id];
        if (
          !existing ||
          new Date(existing.updatedAt).getTime() <= new Date(t.updatedAt).getTime()
        ) {
          next[t.id] = t;
        }
      }
      return { tasksById: next };
    });
  },
  upsert: (task) => {
    set((state) => ({ tasksById: { ...state.tasksById, [task.id]: task } }));
  },
  // Backup-poll reconciliation. The server's `visible_for(user)` response is
  // authoritative for what's still in flight. If a task is locally running
  // but absent here, we missed its final ActionCable broadcast — drop it so
  // the indicator unsticks. Terminal-locally tasks stay until prune.
  reconcile: (tasks) => {
    set((state) => {
      const next: Record<string, BackgroundTask> = {};
      const incoming = new Map(tasks.map((t) => [t.id, t]));
      for (const t of tasks) {
        const existing = state.tasksById[t.id];
        if (
          !existing ||
          new Date(existing.updatedAt).getTime() <= new Date(t.updatedAt).getTime()
        ) {
          next[t.id] = t;
        } else {
          next[t.id] = existing;
        }
      }
      for (const [id, task] of Object.entries(state.tasksById)) {
        if (incoming.has(id)) continue;
        if (task.state === "succeeded" || task.state === "failed") {
          next[id] = task;
        }
      }
      return { tasksById: next };
    });
  },
  prune: () => {
    const now = Date.now();
    const next: Record<string, BackgroundTask> = {};
    for (const [id, task] of Object.entries(get().tasksById)) {
      if (isVisible(task, now)) next[id] = task;
    }
    set({ tasksById: next });
  },
}));

export function selectVisibleTasks(tasksById: Record<string, BackgroundTask>): BackgroundTask[] {
  const now = Date.now();
  return Object.values(tasksById)
    .filter((t) => isVisible(t, now))
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

export function selectActiveTasks(tasksById: Record<string, BackgroundTask>): BackgroundTask[] {
  return Object.values(tasksById)
    .filter((t) => t.state === "queued" || t.state === "running")
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

// Children whose parent is also visible are collapsed into the parent so the
// indicator shows one row per run rather than one row per cascade job.
export function selectVisibleTopLevelTasks(
  tasksById: Record<string, BackgroundTask>,
): BackgroundTask[] {
  const visible = selectVisibleTasks(tasksById);
  const visibleIds = new Set(visible.map((t) => t.id));
  return visible.filter((t) => !t.parentTaskId || !visibleIds.has(t.parentTaskId));
}

export function selectChildren(
  tasksById: Record<string, BackgroundTask>,
  parentId: string,
): BackgroundTask[] {
  return Object.values(tasksById)
    .filter((t) => t.parentTaskId === parentId)
    .sort(
      (a, b) =>
        new Date(a.startedAt ?? a.updatedAt).getTime() -
        new Date(b.startedAt ?? b.updatedAt).getTime(),
    );
}
